import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { renderToStaticMarkup } from "react-dom/server";
import { useTranslation } from "react-i18next";
import PointStartIcon from "../../../shared/icons/PointStartIcon.svg";
import PointFinishIcon from "../../../shared/icons/PointFinishIcon.svg";
import TruckIcon from "../../../shared/icons/TruckIcon.svg";
import MarkerIcon from "../../../shared/icons/MarkerIcon.svg";
import { MapsData } from "../../../../services";
import Loading from "../../../shared/Loading/Loading";
import { useScreenSize } from "../../../../hooks/useScreenSize";
import FullScreenIcon from "../../../shared/icons/FullScreenIcon";
import CloseIconNew from "../../../shared/icons/CloseIconNew";
import { LocateFixedIcon, LocateOffIcon } from "lucide-react";
import { getGpsRouteSegments } from "../../utils/getGpsRouteSegments";

const GpsMarkerIcon = ({ enabled }) => {
  const Icon = enabled ? LocateFixedIcon : LocateOffIcon;

  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="36" height="46" viewBox="0 0 36 46">
      <path
        d="M18 45C15 40 1 29 1 18a17 17 0 1 1 34 0c0 11-14 22-17 27Z"
        fill={enabled ? "#16A34A" : "#FA1861"}
        stroke="white"
        strokeWidth="2"
      />
      <Icon x="8" y="8" size={20} color="white" />
    </svg>
  );
};

const GPS_MARKER_ICONS = {
  true: `data:image/svg+xml;charset=utf-8,${encodeURIComponent(renderToStaticMarkup(<GpsMarkerIcon enabled />))}`,
  false: `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
    renderToStaticMarkup(<GpsMarkerIcon enabled={false} />),
  )}`,
};

const applyCustomStyles = (mapContainer) => {
  const routeButton = mapContainer.querySelector(".ymaps-2-1-79-route-panel__logo");
  if (routeButton) routeButton.style.display = "none";

  const routeContainerBtn = mapContainer.querySelector(".ymaps-2-1-79-map-copyrights-promo");
  if (routeContainerBtn) routeContainerBtn.style.display = "none";

  const innerPanes = document.querySelector(".ymaps-2-1-79-inner-panes");
  if (innerPanes) innerPanes.style.borderRadius = "16px";

  const mapBg = document.querySelector(".ymaps-2-1-79-map-bg");
  if (mapBg) mapBg.style.backgroundImage = "none";

  [
    ".ymaps-2-1-79-copyright__logo",
    ".ymaps-2-1-79-copyright__layout",
    ".ymaps-2-1-79-copyright__content-cell",
    ".ymaps-2-1-79-copyright__content",
  ].forEach((selector) => {
    const el = document.querySelector(selector);
    if (el) el.style.display = "none";
  });
};

const YANDEX_MAPS_SCRIPT_ID = "yandex-maps-api";

const subscribeToYandexMaps = ({ apiKey, language, onReady }) => {
  if (window.ymaps) {
    onReady();
    return () => {};
  }

  let script = document.getElementById(YANDEX_MAPS_SCRIPT_ID);
  if (!script) {
    script = document.createElement("script");
    script.id = YANDEX_MAPS_SCRIPT_ID;
    const mapLang = language === "ru" ? "ru_RU" : "en_US";
    script.src = `https://api-maps.yandex.ru/2.1/?apikey=${apiKey}&lang=${mapLang}`;
    document.body.appendChild(script);
  }

  script.addEventListener("load", onReady);
  return () => script.removeEventListener("load", onReady);
};

const destroyMap = (mapInstanceRef) => {
  mapInstanceRef.current?.destroy();
  mapInstanceRef.current = null;
};

const normalizeCoordinates = (coordinates) => {
  if (typeof coordinates === "string") {
    if (!coordinates.trim()) return null;
    const parsedCoordinates = coordinates.split(",").map(Number);
    return normalizeCoordinates(parsedCoordinates);
  }

  if (!Array.isArray(coordinates) || coordinates.length < 2) return null;

  const lat = Number(coordinates[0]);
  const lng = Number(coordinates[1]);

  return Number.isFinite(lat) && Number.isFinite(lng) ? [lat, lng] : null;
};

const createMap = (containerEl, isModal = false) => {
  if (!containerEl || !window.ymaps) return null;

  const map = new window.ymaps.Map(containerEl, {
    center: [50.813, 71.906],
    zoom: 5,
    controls: isModal ? ["zoomControl", "rulerControl", "geolocationControl"] : ["zoomControl"],
  });

  if (!isModal) {
    map.controls.remove("geolocationControl");
    map.controls.remove("trafficControl");
    map.controls.remove("typeSelector");
    map.controls.remove("fullscreenControl");
    map.controls.remove("rulerControl");
    map.controls.remove("searchControl");
  } else {
    map.controls.add("typeSelector", { position: { top: 10, left: 10 } });
  }

  applyCustomStyles(map.container.getElement());
  return map;
};

const YandexMap = ({
  item,
  cityFrom,
  cityTo,
  routeData,
  isLoadingMap,
  coordinateTruck,
  addressList,
  allowExpand,
}) => {
  const { t, i18n } = useTranslation();
  const mapRef = useRef(null);
  const modalMapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const modalMapInstanceRef = useRef(null);
  const latestRenderMapContentRef = useRef(null);
  const mainContentRendererRef = useRef(null);
  const modalContentRendererRef = useRef(null);
  const YANDEX_API_KEY = process.env.REACT_APP_YMAPS_API_KEY;
  const { isTabletSmallView } = useScreenSize();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const cityFromLatitude = cityFrom?.latitude;
  const cityFromLongitude = cityFrom?.longitude;
  const cityToLatitude = cityTo?.latitude;
  const cityToLongitude = cityTo?.longitude;
  const itemCityIdFrom = item?.cityIdFrom;
  const itemCityIdTo = item?.cityIdTo;

  const renderMapContent = useCallback((map) => {
    if (!map || !window.ymaps) return;

    map.geoObjects.removeAll();

    const hasCityFromCoords = cityFromLatitude != null && cityFromLongitude != null;
    const hasCityToCoords = cityToLatitude != null && cityToLongitude != null;
    const hasBothCityCoords = hasCityFromCoords && hasCityToCoords;
    const getAddressFallbackCoordinates = (isFirstAddress, isLastAddress) => {
      if (isFirstAddress && hasCityFromCoords) {
        return [Number(cityFromLatitude), Number(cityFromLongitude)];
      }

      if (isLastAddress && hasCityToCoords) {
        return [Number(cityToLatitude), Number(cityToLongitude)];
      }

      return null;
    };

    if (coordinateTruck && coordinateTruck.length > 0) {
      const formatDate = (dateStr) => {
        const d = new Date(dateStr);
        return d.toLocaleString("ru-RU", {
          day: "2-digit", month: "2-digit", year: "numeric",
          hour: "2-digit", minute: "2-digit",
        });
      };

      const routeCoordinates = coordinateTruck.map((c) => [c.lat, c.lng]);
      getGpsRouteSegments(coordinateTruck).forEach((segment) => {
        const routeLine = new window.ymaps.Polyline(
          segment.coordinates,
          {},
          { strokeColor: segment.isGpsGap ? "#9CA3AF" : "#000000", strokeWidth: 2, strokeStyle: "2 2" },
        );
        map.geoObjects.add(routeLine);
      });

      coordinateTruck.forEach((coord) => {
        if (typeof coord.gpsEnabledAfter === "boolean") {
          const status = t(`orderInfo.yandexMap.${coord.gpsEnabledAfter ? "gpsEnabled" : "gpsDisabled"}`);
          map.geoObjects.add(
            new window.ymaps.Placemark(
              [coord.lat, coord.lng],
              { hintContent: `${status}: ${formatDate(coord.date)}` },
              {
                iconLayout: "default#image",
                iconImageHref: GPS_MARKER_ICONS[coord.gpsEnabledAfter],
                iconImageSize: [36, 46],
                iconImageOffset: [-18, -46],
                zIndex: 1000,
              },
            ),
          );
          return;
        }

        if (coord.type === 1 || coord.type === 2) return;
        const dot = new window.ymaps.Placemark(
          [coord.lat, coord.lng],
          { hintContent: formatDate(coord.date) },
          {
            iconLayout: "default#image",
            iconImageHref: MarkerIcon,
            iconImageSize: [9, 9],
            iconImageOffset: [-3.5, -3.5],
          },
        );
        map.geoObjects.add(dot);
      });

      const completedPoints = coordinateTruck.filter((c) => c.type === 1 || c.type === 2);
      const startTruckPoint = completedPoints[0];
      if (startTruckPoint) {
        const startPlacemark = new window.ymaps.Placemark(
          [startTruckPoint.lat, startTruckPoint.lng],
          { hintContent: formatDate(startTruckPoint.date) },
          {
            iconLayout: "default#image",
            iconImageHref: PointStartIcon,
            iconImageSize: [30, 42],
            iconImageOffset: [-15, -42],
          },
        );
        map.geoObjects.add(startPlacemark);
      }

      const letters = "BCDEFGHIJKLMNOPQRSTUVWXYZ";
      completedPoints.slice(1).forEach((coord, idx) => {
        const label = letters[idx] || String(idx + 2);
        const marker = new window.ymaps.Placemark(
          [coord.lat, coord.lng],
          { iconContent: label, hintContent: formatDate(coord.date) },
          { preset: "islands#darkBlueStretchyIcon" },
        );
        map.geoObjects.add(marker);
      });

      const incompleteAddresses = (addressList || []).filter((a) => !a.isCompleted);
      if (incompleteAddresses.length > 0) {
        incompleteAddresses.forEach((addr) => {
          const originalIndex = (addressList || []).findIndex((a) => a.id === addr.id);
          const isFirstInOriginal = originalIndex === 0;
          const isLastInOriginal = originalIndex === (addressList || []).length - 1;
          const fallbackCoords = getAddressFallbackCoordinates(isFirstInOriginal, isLastInOriginal);
          const coords = normalizeCoordinates(addr.latLng) || fallbackCoords;
          const addrHint = `${addr.cityName}, ${addr.address}`;

          if (!coords) return;

          if (isFirstInOriginal) {
            map.geoObjects.add(
              new window.ymaps.Placemark(coords, { hintContent: addrHint }, {
                iconLayout: "default#image",
                iconImageHref: PointStartIcon,
                iconImageSize: [30, 42],
                iconImageOffset: [-15, -42],
              }),
            );
          } else if (isLastInOriginal) {
            map.geoObjects.add(
              new window.ymaps.Placemark(coords, { hintContent: addrHint }, {
                iconLayout: "default#image",
                iconImageHref: PointFinishIcon,
                iconImageSize: [30, 42],
                iconImageOffset: [-15, -42],
              }),
            );
          } else {
            const label = letters[originalIndex - 1] || String(originalIndex + 1);
            map.geoObjects.add(
              new window.ymaps.Placemark(
                coords,
                { iconContent: label, hintContent: addrHint },
                { preset: "islands#darkBlueStretchyIcon" },
              ),
            );
          }
        });
      }

      const lastPoint = coordinateTruck[coordinateTruck.length - 1];
      map.geoObjects.add(
        new window.ymaps.Placemark(
          [lastPoint.lat, lastPoint.lng],
          { hintContent: `${t("orderInfo.yandexMap.lastUpdate")}: ${formatDate(lastPoint.date)}` },
          {
            iconLayout: "default#image",
            iconImageHref: TruckIcon,
            iconImageSize: [30, 42],
            iconImageOffset: typeof lastPoint.gpsEnabledAfter === "boolean" ? [20, -21] : [-15, -21],
          },
        ),
      );

      const allBoundsPlacemarks = routeCoordinates.map((c) => new window.ymaps.Placemark(c));
      incompleteAddresses.forEach((addr) => {
        const originalIndex = (addressList || []).findIndex((a) => a.id === addr.id);
        const isFirstInOriginal = originalIndex === 0;
        const isLastInOriginal = originalIndex === (addressList || []).length - 1;
        const fallbackCoords = getAddressFallbackCoordinates(isFirstInOriginal, isLastInOriginal);
        const coords = normalizeCoordinates(addr.latLng) || fallbackCoords;
        if (!coords) return;
        allBoundsPlacemarks.push(new window.ymaps.Placemark(coords));
      });
      const bounds = window.ymaps.geoQuery(allBoundsPlacemarks).getBounds();
      if (bounds) map.setBounds(bounds, { checkZoomRange: true, duration: 500 });
    } else if (
      !isLoadingMap &&
      (addressList || []).length > 2 &&
      (addressList || []).every((point) => point.latLng != null)
    ) {
      const referencePoints = [...(addressList || []).map((point) => point.latLng)];
      const multiRoute = new window.ymaps.multiRouter.MultiRoute(
        { referencePoints, params: { routingMode: "truck" } },
        {
          boundsAutoApply: true,
          wayPointStartIconLayout: "default#image",
          wayPointFinishIconLayout: "default#image",
          wayPointStartIconImageHref: PointStartIcon,
          wayPointFinishIconImageHref: PointFinishIcon,
          wayPointStartIconImageSize: [30, 42],
          wayPointStartIconImageOffset: [-15, -42],
          wayPointFinishIconImageSize: [30, 42],
          wayPointFinishIconImageOffset: [-15, -42],
          routeStrokeWidth: 2,
          routeActiveStrokeWidth: 4,
          routeActiveStrokeColor: "#0000FF",
        },
      );
      map.geoObjects.add(multiRoute);
    } else if (routeData) {
      const savedRoute = new window.ymaps.Polyline(
        routeData,
        {},
        { strokeColor: "#C5115E", strokeWidth: 3 },
      );
      map.geoObjects.add(savedRoute);

      map.geoObjects.add(
        new window.ymaps.Placemark(routeData[0], {}, {
          iconLayout: "default#image",
          iconImageHref: PointStartIcon,
          iconImageSize: [30, 42],
          iconImageOffset: [-15, -42],
        }),
      );
      map.geoObjects.add(
        new window.ymaps.Placemark(routeData[routeData.length - 1], {}, {
          iconLayout: "default#image",
          iconImageHref: PointFinishIcon,
          iconImageSize: [30, 42],
          iconImageOffset: [-15, -42],
        }),
      );

      const routeBounds = savedRoute.geometry.getBounds();
      if (routeBounds) map.setBounds(routeBounds, { checkZoomRange: true, duration: 500 });
    } else if (hasBothCityCoords) {
      const multiRoute = new window.ymaps.multiRouter.MultiRoute(
        {
          referencePoints: [
            [cityFromLatitude, cityFromLongitude],
            [cityToLatitude, cityToLongitude],
          ],
          params: { routingMode: "truck", truckWeight: 10000 },
        },
        {
          boundsAutoApply: true,
          wayPointStartIconLayout: "default#image",
          wayPointFinishIconLayout: "default#image",
          wayPointStartIconImageHref: PointStartIcon,
          wayPointFinishIconImageHref: PointFinishIcon,
          wayPointStartIconImageSize: [30, 42],
          wayPointStartIconImageOffset: [-15, -42],
          wayPointFinishIconImageSize: [30, 42],
          wayPointFinishIconImageOffset: [-15, -42],
          routeStrokeWidth: 2,
          routeActiveStrokeWidth: 4,
          routeActiveStrokeColor: "#0000FF",
        },
      );

      multiRoute.events.add("update", function () {
        const mc = map.container.getElement();
        const rb = mc.querySelector(".ymaps-2-1-79-route-panel__logo");
        if (rb) rb.style.display = "none";
        const rcb = mc.querySelector(".ymaps-2-1-79-map-copyrights-promo");
        if (rcb) rcb.style.display = "none";
        const ip = document.querySelector(".ymaps-2-1-79-inner-panes");
        if (ip) ip.style.borderRadius = "16px";
        const mb = document.querySelector(".ymaps-2-1-79-map-bg");
        if (mb) mb.style.backgroundImage = "none";
      });

      map.geoObjects.add(multiRoute);

      multiRoute.model.events.add("requestsuccess", function () {
        const activeRoute = multiRoute.getActiveRoute();
        if (activeRoute) {
          const distance = activeRoute.properties.get("distance").value;
          const duration = activeRoute.properties.get("duration").value;
          const distanceInKm = (distance / 1000).toFixed(2);
          const formatDuration = (dur) => {
            const hours = Math.floor(dur / 3600).toString().padStart(2, "0");
            const minutes = Math.ceil((dur % 3600) / 60).toString().padStart(2, "0");
            return `${hours}:${minutes}`;
          };
          const routePaths = activeRoute.getPaths().toArray().map((path) => path.getSegments().toArray());
          const flattenedCoordinates = routePaths.flat(2).map((segment) => segment.geometry.getCoordinates()).flat();

          if (itemCityIdFrom && itemCityIdTo) {
            MapsData.sendRoute({
              fromCityId: itemCityIdFrom,
              toCityId: itemCityIdTo,
              jsonData: JSON.stringify(flattenedCoordinates),
              distance: distanceInKm,
              totalTime: formatDuration(duration),
            });
          }
        }
      });
    }

  }, [
    addressList,
    cityFromLatitude,
    cityFromLongitude,
    cityToLatitude,
    cityToLongitude,
    coordinateTruck,
    isLoadingMap,
    itemCityIdFrom,
    itemCityIdTo,
    routeData,
    t,
  ]);

  latestRenderMapContentRef.current = renderMapContent;

  // Main map
  useEffect(() => {
    if (isLoadingMap) return undefined;

    let isCancelled = false;

    const initMap = () => {
      if (isCancelled || !window.ymaps) return;

      window.ymaps.ready(() => {
        if (isCancelled || !mapRef.current) return;

        const map = createMap(mapRef.current, false);
        mapInstanceRef.current = map;

        const renderContent = latestRenderMapContentRef.current;
        renderContent?.(map);
        mainContentRendererRef.current = renderContent;
      });
    };

    const unsubscribe = subscribeToYandexMaps({
      apiKey: YANDEX_API_KEY,
      language: i18n.language,
      onReady: initMap,
    });

    return () => {
      isCancelled = true;
      unsubscribe();
      destroyMap(mapInstanceRef);
      mainContentRendererRef.current = null;
    };
  }, [YANDEX_API_KEY, i18n.language, isLoadingMap]);

  // Modal map — initializes after modal is mounted
  useEffect(() => {
    if (!isModalOpen) return;

    let isCancelled = false;

    const initMap = () => {
      if (isCancelled || !window.ymaps) return;

      window.ymaps.ready(() => {
        if (isCancelled || !modalMapRef.current) return;
        const map = createMap(modalMapRef.current, true);
        modalMapInstanceRef.current = map;

        const renderContent = latestRenderMapContentRef.current;
        renderContent?.(map);
        modalContentRendererRef.current = renderContent;
      });
    };

    const unsubscribe = subscribeToYandexMaps({
      apiKey: YANDEX_API_KEY,
      language: i18n.language,
      onReady: initMap,
    });

    return () => {
      isCancelled = true;
      unsubscribe();
      destroyMap(modalMapInstanceRef);
      modalContentRendererRef.current = null;
    };
  }, [YANDEX_API_KEY, i18n.language, isModalOpen]);

  useEffect(() => {
    if (isLoadingMap) return;

    const updateContent = (mapInstance, contentRendererRef) => {
      if (!mapInstance || contentRendererRef.current === renderMapContent) return;

      renderMapContent(mapInstance);
      contentRendererRef.current = renderMapContent;
    };

    updateContent(mapInstanceRef.current, mainContentRendererRef);
    updateContent(modalMapInstanceRef.current, modalContentRendererRef);
  }, [isLoadingMap, renderMapContent]);

  return (
    <div style={{ width: "100%", height: "100%", position: "relative" }}>
      {isLoadingMap ? (
        <Loading height="100%" borderRadius="16px" />
      ) : (
        <div
          ref={mapRef}
          style={{
            width: isTabletSmallView ? "calc(100vw - 32px)" : "100%",
            height: isTabletSmallView ? "70vw" : "100%",
          }}
        />
      )}

      {allowExpand && !isLoadingMap && (
        <button
          onClick={() => setIsModalOpen(true)}
          style={{
            position: "absolute",
            bottom: 12,
            right: 12,
            display: "inline-flex",
            alignItems: "center",
            gap: 10,
            borderRadius: 10,
            background: "#FFF",
            border: "none",
            padding: "8px",
            cursor: "pointer",
            zIndex: 10,
            boxShadow: "0 2px 8px rgba(0,0,0,0.18)",
          }}
        >
          <FullScreenIcon />
        </button>
      )}

      {isModalOpen && ReactDOM.createPortal(
        <div
          data-portal-modal="true"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.55)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            if (e.target === e.currentTarget) setIsModalOpen(false);
          }}
        >
          <div
            style={{
              position: "relative",
              width: "88vw",
              height: "82vh",
              background: "#fff",
              borderRadius: 16,
              overflow: "hidden",
            }}
          >
            <button
              onClick={() => setIsModalOpen(false)}
              style={{
                position: "absolute",
                top: 12,
                right: 12,
                zIndex: 10,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: 10,
                background: "#FFF",
                border: "none",
                padding: "6px",
                cursor: "pointer",
                boxShadow: "0 2px 8px rgba(0,0,0,0.18)",
              }}
            >
              <CloseIconNew />
            </button>
            <div
              ref={modalMapRef}
              data-testid="yandex-map-fullscreen-map"
              style={{ width: "100%", height: "100%" }}
            />
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default YandexMap;
